import React, { useState, useEffect } from 'react';
import Layout from '@/components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from '@/components/ui/tabs';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Handshake, Clock, CheckCircle, Send, DollarSign, Package,
  Eye, ThumbsUp, ThumbsDown
} from 'lucide-react';
import api from '@/services/api';
import { toast } from 'sonner';
import InfoTip from '@/components/InfoTip';
import { useCurrency } from '@/context/CurrencyContext';

const OneSourcePage = () => {
  const { format } = useCurrency();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const res = await api.get('/actions/source/requests');
      const apiRequests = res.data.requests || [];
      if (apiRequests.length === 0) {
        setRequests([
          { request_id: 'src_001', context: { mpn: 'NRF52840-QIAA-R' }, details: 'Need 2000 units, target $4.80, authorized distributors preferred', priority: 'urgent', status: 'pending', created_at: '2026-03-02T11:00:00Z', quantity: 2000, packaging: 'Reel', distributor_type: 'authorized', terms: 'Net 30' },
          { request_id: 'src_002', context: { mpn: 'ESP32-WROOM-32E' }, details: 'Source 5000 units, target $2.20, CIF delivery', priority: 'medium', status: 'pending', created_at: '2026-02-25T14:30:00Z', quantity: 5000, packaging: 'Tray', distributor_type: 'authorized', terms: 'Net 45' },
          { request_id: 'src_003', context: { mpn: 'MT48LC16M16A2P-6A:G' }, details: 'Urgently need 1000 units — going NRND, qualify alternate', priority: 'high', status: 'negotiated', created_at: '2026-02-20T09:00:00Z', quantity: 1000, packaging: 'Tube', negotiated_rate: 2.60, original_rate: 3.25, distributor_type: 'authorized', terms: 'Net 30' },
        ]);
      } else {
        setRequests(apiRequests);
      }
    } catch {
      setRequests([
        { request_id: 'src_001', context: { mpn: 'NRF52840-QIAA-R' }, details: 'Need 2000 units, target $4.80', priority: 'urgent', status: 'pending', created_at: '2026-03-02T11:00:00Z' },
      ]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchRequests(); }, []);

  const pendingReqs = requests.filter(r => r.status === 'pending');
  const negotiatedReqs = requests.filter(r => ['negotiated', 'completed'].includes(r.status));

  const proactiveOffers = [
    { id: 'pro_1', mpn: 'STM32F407VGT6', qty: 25000, oldRate: 12.50, newRate: 9.80, paymentTerms: 'Net 30', leadTime: '10 days', savings: 21.6, savingsValue: 67500, status: 'awaiting_approval' },
    { id: 'pro_2', mpn: 'GRM188R71H104KA93D', qty: 100000, oldRate: 0.028, newRate: 0.018, paymentTerms: 'Net 45', leadTime: '5 days', savings: 35.7, savingsValue: 1000, status: 'awaiting_approval' },
    { id: 'pro_3', mpn: 'ESP32-WROOM-32E', qty: 22000, oldRate: 3.20, newRate: 2.45, paymentTerms: 'Net 30', leadTime: '7 days', savings: 23.4, savingsValue: 16500, status: 'awaiting_approval' },
    { id: 'pro_4', mpn: 'MT48LC16M16A2', qty: 20000, oldRate: 3.50, newRate: 2.80, paymentTerms: 'Net 60', leadTime: '14 days', savings: 20.0, savingsValue: 14000, status: 'awaiting_approval' },
  ];

  const totalSavingsOpportunity = proactiveOffers.reduce((s, o) => s + o.savingsValue, 0);

  const statusBadge = (s) => ({
    pending: <Badge className="bg-yellow-100 text-yellow-800"><Clock className="h-3 w-3 mr-1" />In Progress</Badge>,
    negotiated: <Badge className="bg-blue-100 text-blue-800"><CheckCircle className="h-3 w-3 mr-1" />Negotiated</Badge>,
    completed: <Badge className="bg-green-100 text-green-800"><CheckCircle className="h-3 w-3 mr-1" />Completed</Badge>,
    awaiting_approval: <Badge className="bg-purple-100 text-purple-800"><Eye className="h-3 w-3 mr-1" />Awaiting Approval</Badge>,
  }[s] || <Badge variant="outline">{s}</Badge>);

  return (
    <Layout title="1Source" subtitle="Let our AI agents negotiate the best rates for you">
      <div className="space-y-6" data-testid="one-source-page">
        {/* Summary — 3 KPIs as per requirement */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="bg-purple-50 border-purple-200 shadow-sm" data-testid="proactive-offers-kpi">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-purple-600 flex items-center">Proactive Offers <InfoTip text="Rates we proactively negotiated for you — review and approve to proceed." /></p>
                  <p className="text-2xl font-bold text-purple-700">{proactiveOffers.length}</p>
                </div>
                <DollarSign className="h-10 w-10 text-purple-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-blue-50 border-blue-200 shadow-sm" data-testid="your-requests-kpi">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-blue-600 flex items-center">Your Request Offers <InfoTip text="Sourcing requests you submitted that are currently being negotiated by our agents." /></p>
                  <p className="text-2xl font-bold text-blue-700">{pendingReqs.length + negotiatedReqs.length}</p>
                </div>
                <Send className="h-10 w-10 text-blue-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-green-50 border-green-200 shadow-sm" data-testid="savings-opportunity-kpi">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-green-600 flex items-center">Value Saving Opportunity <InfoTip text="Total savings across all proactive and negotiated offers if approved." /></p>
                  <p className="text-2xl font-bold text-green-700">{format(totalSavingsOpportunity)}</p>
                </div>
                <Package className="h-10 w-10 text-green-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs — Proactive first */}
        <Tabs defaultValue="proactive">
          <TabsList className="grid w-full grid-cols-3 max-w-lg">
            <TabsTrigger value="proactive" data-testid="proactive-tab">Proactive Offers</TabsTrigger>
            <TabsTrigger value="active">Your Requests</TabsTrigger>
            <TabsTrigger value="completed">Completed</TabsTrigger>
          </TabsList>

          {/* Proactive Offers — now first tab, with savings value */}
          <TabsContent value="proactive" className="mt-4">
            <Card className="bg-white border-gray-200 shadow-sm">
              <CardHeader>
                <CardTitle className="text-gray-900 text-base flex items-center gap-2">
                  <Handshake className="h-4 w-4 text-green-600" /> Proactive Negotiations
                  <InfoTip text="Rates we proactively negotiated for you. Review the savings and approve to proceed." />
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {proactiveOffers.map((offer, idx) => (
                    <div key={idx} className="p-4 border border-gray-200 rounded-lg hover:border-green-300 transition-all" data-testid={`proactive-offer-${idx}`}>
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <span className="font-mono font-bold text-gray-900">{offer.mpn}</span>
                          {statusBadge(offer.status)}
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge className="bg-green-100 text-green-800 text-lg px-3 py-1">-{offer.savings}%</Badge>
                          <Badge className="bg-green-600 text-white px-3 py-1">{format(offer.savingsValue)}</Badge>
                        </div>
                      </div>
                      <div className="grid grid-cols-5 gap-4 mb-3">
                        <div><p className="text-xs text-gray-500">Your Rate</p><p className="font-semibold text-gray-900 line-through">{format(offer.oldRate)}</p></div>
                        <div><p className="text-xs text-gray-500">Negotiated Rate</p><p className="font-bold text-green-700">{format(offer.newRate)}</p></div>
                        <div><p className="text-xs text-gray-500">Qty</p><p className="text-sm font-medium text-gray-900">{offer.qty.toLocaleString()}</p></div>
                        <div><p className="text-xs text-gray-500">Payment Terms</p><p className="text-sm text-gray-900">{offer.paymentTerms}</p></div>
                        <div><p className="text-xs text-gray-500">Lead Time</p><p className="text-sm text-gray-900">{offer.leadTime}</p></div>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-gray-500 mb-3">
                        <span>Distributor: <strong>Authorized T1 Distributor</strong> (name disclosed upon approval)</span>
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white" data-testid={`approve-offer-${idx}`}>
                          <ThumbsUp className="h-3.5 w-3.5 mr-1" /> Approve & Generate PO
                        </Button>
                        <Button size="sm" variant="outline" className="text-gray-600">
                          <ThumbsDown className="h-3.5 w-3.5 mr-1" /> Decline
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Your Requests */}
          <TabsContent value="active" className="mt-4">
            <Card className="bg-white border-gray-200 shadow-sm">
              <CardContent className="pt-4">
                {loading ? (
                  <div className="flex items-center justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600" /></div>
                ) : pendingReqs.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <Send className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                    <p>No active sourcing requests.</p>
                    <p className="text-sm mt-1">Use the "Refer to 1Source" button across the platform to send us parts to negotiate.</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gray-50">
                        <TableHead>Component</TableHead>
                        <TableHead>Details</TableHead>
                        <TableHead>Priority</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Submitted</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {pendingReqs.map((req, idx) => (
                        <TableRow key={idx} data-testid={`source-request-${idx}`}>
                          <TableCell className="font-mono text-sm font-medium">{req.context?.mpn || 'Multiple'}</TableCell>
                          <TableCell className="text-sm text-gray-600 max-w-xs truncate">{req.details}</TableCell>
                          <TableCell><Badge variant="outline" className="capitalize">{req.priority}</Badge></TableCell>
                          <TableCell>{statusBadge(req.status)}</TableCell>
                          <TableCell className="text-sm text-gray-500">{new Date(req.created_at).toLocaleDateString()}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Completed */}
          <TabsContent value="completed" className="mt-4">
            <Card className="bg-white border-gray-200 shadow-sm">
              <CardContent className="pt-6 text-center py-12 text-gray-500">
                <CheckCircle className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                <p>Completed negotiations will appear here for reference.</p>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default OneSourcePage;
